import React, { useState, useEffect, useMemo } from 'react';
import { CarOutlined, CheckOutlined, LeftOutlined, RightOutlined, DownOutlined, LoadingOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { message, Input, Select } from 'antd';
import axios from '../../../../../utils/axiosConfig';
import { useAuth } from '../../../../../contexts/AuthContext';
import CustomerCSVUpload from './CustomerCSVUpload';


const rangeOptions = [
  { value: 500, label: '500 ft', unit: 'ft' },
  { value: 1000, label: '1000 ft', unit: 'ft' },
  { value: 2500, label: '2500 ft', unit: 'ft' },
  { value: 5000, label: '5000 ft', unit: 'ft' },
  { value: 5280, label: '1 mi', unit: 'mi' },
  { value: 10560, label: '2 mi', unit: 'mi' },
  { value: 26400, label: '5 mi', unit: 'mi' },
];

const ConfigureProximityIQ = ({ campaignData, onUpdate, onNext, isAdminCreating, targetUserId }) => {
  const { currentUser, token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const [uploadedCustomers, setUploadedCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const vehiclesPerPage = 6;

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});
  
  // Check if user is enterprise/agency
  const isEnterprise = currentUser?.role === 'agency' || currentUser?.role === 'enterprise';

  // Fetch vehicles - either from assignments (enterprise) or Geotab (client)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // For enterprise users, fetch assigned vehicles from our API
        if (isEnterprise && !isAdminCreating) {
          console.log('[ConfigureProximityIQ] Enterprise mode - fetching assigned vehicles');
          const [assignmentsRes, workspacesRes, accountsRes] = await Promise.all([
            axios.get('/api/vehicle-tracking/assignments', { headers }),
            axios.get('/api/vehicle-tracking/workspaces', { headers }),
            axios.get('/api/integrations/geotab/accounts', { headers })
          ]);
          
          const assignments = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : (assignmentsRes.data.items || []);
          const workspaces = Array.isArray(workspacesRes.data) ? workspacesRes.data : (workspacesRes.data.items || []);
          const accounts = Array.isArray(accountsRes.data) ? accountsRes.data : (accountsRes.data.items || []);
          const databases = accounts.map(a => a?.database).filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i);
          
          let filteredAssignments = assignments;
          if (campaignData.selectedLocations?.length > 0) {
            filteredAssignments = assignments.filter(a => 
              campaignData.selectedLocations.some(loc => Number(loc) === Number(a.workspace_id))
            );
          }
          
          let devices = [];
          if (databases.length > 0) {
            try {
              const devicesRes = await axios.get(`/api/integrations/geotab/devices?databases=${databases.join(',')}`, { headers });
              devices = Array.isArray(devicesRes.data?.devices) ? devicesRes.data.devices : [];
            } catch (devErr) {
              console.error('[ConfigureProximityIQ] Error fetching devices:', devErr);
            }
          }
          
          const assignedVehicles = filteredAssignments.map(assignment => {
            const device = devices.find(d => d.id === assignment.geotab_device_id);
            const workspace = workspaces.find(w => Number(w.id) === Number(assignment.workspace_id));
            return {
              id: assignment.geotab_device_id,
              name: device?.name || `Vehicle ${assignment.geotab_device_id}`,
              location: workspace?.name || 'Unassigned'
            };
          });
          
          console.log('[ConfigureProximityIQ] Assigned vehicles:', assignedVehicles.length);
          setVehicles(assignedVehicles);
          setGeotabDatabases(databases.length > 0 ? databases : ['assigned']);
          return;
        }
        
        // For client users, fetch their assigned vehicles
        if (!isAdminCreating) {
          console.log('[ConfigureProximityIQ] Client mode - fetching assigned vehicles');
          try {
            const assignmentsRes = await axios.get('/api/client-vehicle-assignments/my-vehicles', { headers });
            const assignments = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : (assignmentsRes.data.items || []);
            
            // Get Geotab accounts for device details
            const accountsRes = await axios.get('/api/integrations/geotab/accounts', { headers });
            const accounts = Array.isArray(accountsRes.data) ? accountsRes.data : (accountsRes.data.items || []);
            const databases = accounts.map(a => a?.database).filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i);
            
            let devices = [];
            if (databases.length > 0) {
              try {
                const devicesRes = await axios.get(`/api/integrations/geotab/devices?databases=${databases.join(',')}`, { headers });
                devices = Array.isArray(devicesRes.data?.devices) ? devicesRes.data.devices : [];
              } catch (devErr) {
                console.error('[ConfigureProximityIQ] Error fetching devices:', devErr);
              }
            }
            
            const assignedVehicles = assignments.map(assignment => {
              const device = devices.find(d => d.id === assignment.geotab_device_id);
              return {
                id: assignment.geotab_device_id,
                name: device?.name || `Vehicle ${assignment.geotab_device_id}`,
                location: assignment.location_name || device?.comment || 'Assigned Location',
                code: device?.serialNumber || device?.licensePlate || assignment.geotab_device_id
              };
            });
            
            console.log('[ConfigureProximityIQ] Client assigned vehicles:', assignedVehicles.length);
            setVehicles(assignedVehicles);
            setGeotabDatabases(databases.length > 0 ? databases : ['assigned']);
            return;
          } catch (err) {
            console.error('[ConfigureProximityIQ] Error fetching client assignments:', err);
            // Fall back to regular Geotab loading if assignments fail
          }
        }
        
        // Fallback: Fetch Geotab accounts (for admin creating or if assignments fail)
        const endpoint = isAdminCreating && targetUserId 
          ? `/api/integrations/geotab/accounts?userId=${targetUserId}`
          : '/api/integrations/geotab/accounts';
        
        const { data } = await axios.get(endpoint, { headers });
        const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        const databases = items
          .map(a => a?.database)
          .filter(Boolean)
          .filter((v, i, arr) => arr.indexOf(v) === i);
        
        console.log('[ConfigureProximityIQ] Fallback mode - databases found:', databases);
        setGeotabDatabases(databases);
      } catch (e) {
        console.error('Error fetching data:', e);
        setGeotabDatabases([]);
      }
    };

    fetchData();
  }, [currentUser, token, isAdminCreating, targetUserId, isEnterprise, campaignData.selectedLocations]);

  const fetchVehicles = async () => {
    if (!geotabDatabases.length) {
      message.warning('No Geotab databases configured');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      query.set('databases', geotabDatabases.join(','));
      const { data } = await axios.get(`/api/integrations/geotab/devices?${query.toString()}`, { headers: authHeader() });
      const items = Array.isArray(data?.devices) ? data.devices : [];
      
      // Transform Geotab device data to match our component structure
      const transformedVehicles = items.map((device) => {
        // Try to get location from groups, comment, or use device identifiers
        const locationName = device.groups?.[0]?.name || 
                            device.comment || 
                            device.licensePlate || 
                            device.serialNumber || 
                            'Unknown Location';
        
        // Get vehicle code from serial number or VIN
        const vehicleCode = device.serialNumber || 
                           device.vehicleIdentificationNumber || 
                           device.licensePlate || 
                           device.id;
        
        return {
          id: device.id,
          name: device.name || `Vehicle ${device.id}`,
          location: locationName,
          code: vehicleCode
        };
      });
      
      setVehicles(transformedVehicles);
      
      if (transformedVehicles.length > 0) {
        message.success(`Loaded ${transformedVehicles.length} vehicle(s) from Geotab`);
      } else {
        message.info('No vehicles found in Geotab');
      }
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to load vehicles');
      message.error('Failed to load vehicles from Geotab');
    } finally {
      setLoading(false);
    }
  };

  // Get unique locations for filter
  const uniqueLocations = useMemo(() => {
    const locations = vehicles.map(v => v.location).filter(Boolean).filter(loc => loc !== 'N/A');
    return ['all', ...Array.from(new Set(locations))];
  }, [vehicles]);

  // Filter vehicles based on search and location filter
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      const matchesSearch = !searchQuery || 
        vehicle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vehicle.location && vehicle.location.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesLocation = filterLocation === 'all' || vehicle.location === filterLocation;
      
      return matchesSearch && matchesLocation;
    });
  }, [vehicles, searchQuery, filterLocation]);

  const totalPages = Math.ceil(filteredVehicles.length / vehiclesPerPage);
  const startIndex = (currentPage - 1) * vehiclesPerPage;
  const visibleVehicles = filteredVehicles.slice(startIndex, startIndex + vehiclesPerPage);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterLocation]);

  const selectedVehicles = campaignData.selectedVehicles || [];
  const notificationRange = campaignData.notificationRange || 1000;

  const toggleVehicle = (vehicleId) => {
    const isSelected = selectedVehicles.includes(vehicleId);
    if (isSelected) {
      onUpdate({ selectedVehicles: selectedVehicles.filter(id => id !== vehicleId) });
    } else {
      onUpdate({ selectedVehicles: [...selectedVehicles, vehicleId] });
    }
  };

  const selectAllVehicles = () => {
    const allIds = filteredVehicles.map(v => v.id);
    onUpdate({ selectedVehicles: allIds });
  };

  const unselectAllVehicles = () => {
    onUpdate({ selectedVehicles: [] });
  };

  const handleRangeChange = (range) => {
    onUpdate({ notificationRange: range });
  };

  const handleCustomersLoaded = (customers) => {
    setUploadedCustomers(customers);
    onUpdate({ uploadedCustomers: customers });
  };

  const handleClearCustomers = () => {
    setUploadedCustomers([]);
    onUpdate({ uploadedCustomers: [] });
  };

  const handleContinue = () => {
    if (selectedVehicles.length > 0) {
      onNext();
    }
  };

  const getRangeLabel = (value) => {
    const option = rangeOptions.find(o => o.value === value);
    return option ? option.label : `${value} ft`;
  };

  // Show initial state with Load Vehicles button
  if (vehicles.length === 0 && !loading && !error) {
    return (
      <div className="step-container">
        <h1 className="step-title">Configure ProximityIQ Triggers</h1>
        <p className="step-subtitle">Select vehicles and set the notification range for customer alerts</p>
        
        <div style={{ 
          background: '#f0f5ff', 
          border: '1px solid #adc6ff', 
          borderRadius: '12px', 
          padding: '40px', 
          textAlign: 'center',
          marginTop: '40px',
          maxWidth: '600px',
          margin: '40px auto'
        }}>
          <p style={{ color: 'var(--ink)', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Load Your Vehicles</p>
          <p style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '24px' }}>
            {isEnterprise 
              ? 'Click the button below to fetch your assigned vehicles'
              : 'Click the button below to fetch your assigned vehicles from Geotab'
            }
          </p>
          <button 
            className="continue-btn" 
            onClick={fetchVehicles}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load Assigned Vehicles'}
          </button>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && vehicles.length === 0) {
    return (
      <div className="step-container">
        <h1 className="step-title">Configure ProximityIQ Triggers</h1>
        <div style={{ 
          background: '#fff2f0', 
          border: '1px solid #ffccc7', 
          borderRadius: '12px', 
          padding: '40px', 
          textAlign: 'center',
          marginTop: '40px',
          maxWidth: '600px',
          margin: '40px auto'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ color: '#cf1322', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Unable to load vehicles</p>
          <p style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '24px' }}>{error}</p>
          <button 
            className="continue-btn" 
            onClick={fetchVehicles}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <h1 className="step-title">Configure ProximityIQ Triggers</h1>
      <p className="step-subtitle">Select vehicles and set the notification range for customer alerts</p>

      <div className="configure-container proximity-config">
        {/* Left Side: Vehicles Selection and Notification Range */}
        <div className="vehicles-section">
          <div className="section-header">
            <div>
              <span className="section-title">Select Vehicles ({filteredVehicles.length})</span>
              <p className="section-subtitle">Choose vehicles that will trigger proximity campaigns</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                className="select-all-btn-outlined" 
                onClick={fetchVehicles}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {loading ? <LoadingOutlined /> : '🔄'} Reload
              </button>
              <button className="select-all-btn-outlined" onClick={selectAllVehicles}>
                Select All
              </button>
              <button className="select-all-btn-outlined" onClick={unselectAllVehicles}>
                Unselect All
              </button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
            <Input
              placeholder="Search vehicles by name or location..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ flex: 1 }}
            />
            <Select
              value={filterLocation}
              onChange={setFilterLocation}
              style={{ width: 200 }}
              suffixIcon={<FilterOutlined />}
            >
              {uniqueLocations.map(loc => (
                <Select.Option key={loc} value={loc}>
                  {loc === 'all' ? 'All Locations' : loc}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="vehicles-grid two-col">
            {visibleVehicles.map((vehicle) => {
              const isSelected = selectedVehicles.includes(vehicle.id);
              return (
                <div
                  key={vehicle.id}
                  className={`vehicle-card proximity-vehicle ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleVehicle(vehicle.id)}
                >
                  <div className="vehicle-icon">
                    <CarOutlined />
                  </div>
                  <div className="vehicle-info">
                    <div className="vehicle-name">{vehicle.name}</div>
                    <div className="vehicle-location">{vehicle.location} • {vehicle.code}</div>
                  </div>
                  {isSelected && (
                    <div className="vehicle-check">
                      <CheckOutlined />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination compact">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <LeftOutlined />
              </button>
              <span>{currentPage} / {totalPages}</span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <RightOutlined />
              </button>
            </div>
          )}

          {/* Notification Range Section - Below Vehicles */}
          <div style={{ marginTop: '24px' }}>
            <div className="config-panel">
              <label className="config-label">Notification Range</label>
              <p className="config-sublabel">Distance to trigger customer alerts</p>
              
              <div className="range-options-grid">
                {rangeOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`range-option ${notificationRange === option.value ? 'selected' : ''}`}
                    onClick={() => handleRangeChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <p className="range-description">
                When a vehicle enters within <strong>{getRangeLabel(notificationRange)}</strong> of a customer, they'll receive your message.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side: Customer CSV Upload */}
        <div className="config-section">
          <CustomerCSVUpload
            uploadedCustomers={uploadedCustomers}
            onCustomersLoaded={handleCustomersLoaded}
            onClearCustomers={handleClearCustomers}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          {selectedVehicles.length} vehicles • {getRangeLabel(notificationRange)} range
          {uploadedCustomers.length > 0 && ` • ${uploadedCustomers.length} customers uploaded`}
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
          disabled={selectedVehicles.length === 0}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default ConfigureProximityIQ;
